import { Box, Typography } from '@mui/material'
import { displayMultipleTitle, getSanitizedValue, toCamelCase } from '../../../Helpers/Util'
import { IFieldTitleValue, IKeyValue } from '../../../Models/CommonListHeadingModal'
import RadioInputComponent from '../../Shared/RadioInput'
import CONSTANTS from '../../../Constants'
import { FieldValues, FormProvider } from 'react-hook-form'
import { lazy, Suspense } from 'react'
import { Loader, MUIButton, MUICheckbox } from '../../Shared'
import { DisplayTabDetailAndFormProps, RenderFilterList } from '../../../Models/CategoryFormData'
import { ComplexFieldView } from '../../ComplexView'
import Styles from './CommonHelperFunctions.module.scss'

const DynamicFormCreation = lazy(() => import('../../Atoms/DynamicFormCreation'))

export const getTitleValue = (title: string, value: string, required?: boolean) => {
  return (
    <>
      <Box color='#6A6C6D' width='192px'>
        {title}
        {required && <span style={{ color: '#D4351C', margin: 0 }}>*</span>}
      </Box>
      <Box
        className='font-semibold word-break-all'
        width='calc(100% - 192px)'
        paddingLeft='0.75rem'
        dangerouslySetInnerHTML={{
          __html: getSanitizedValue(Array.isArray(value) ? displayMultipleTitle(value) : value),
        }}
      />
    </>
  )
}
export const displayFieldTitleValue = (data: IFieldTitleValue, isLastField: boolean) => {
  const { title, value, required } = data

  return (
    <Box
      data-testid={toCamelCase(title)}
      key={title}
      width='100%'
      display='flex'
      alignItems='baseline'
      fontSize='14px'
      borderBottom={isLastField ? 0 : '1px solid rgba(0, 0, 0, 0.16)'}
      padding='1.25rem 0.75rem'
    >
      {getTitleValue(title, value, required)}
    </Box>
  )
}
export const displayParameterData = (formData: IFieldTitleValue[]) => {
  if (formData?.length === 0) return null
  return formData?.map((data: any, index: number) => {
    if (data?.parameterRef?.length > 0) {
      return (
        <div
          key={data?.fieldName}
          style={{
            borderBottom: index === formData?.length - 1 ? 'none' : '1px solid rgba(0, 0, 0, 0.16)',
            width: '100%',
          }}
        >
          <h3 className='complex-type-field-title'>{data.title}</h3>
          <ComplexFieldView data={data.parameterRef} />
        </div>
      )
    } else {
      return displayFieldTitleValue(data, index === formData.length - 1)
    }
  })
}
export const displayTabs = (
  dataTabs: IKeyValue[],
  selectedTab: IKeyValue,
  onClick: (tab: IKeyValue) => void,
) => {
  if (
    (dataTabs?.length === 0 || (dataTabs.length === 1 && selectedTab?.key === 'default')) &&
    selectedTab?.key === ''
  )
    return null
  return dataTabs.map((tab) => {
    const isSelectedTab = tab.key === selectedTab.key
    return (
      <div
        data-testid={`tab_${toCamelCase(tab.key)}`}
        className='font-semibold'
        key={toCamelCase(tab.key)}
        aria-hidden='true'
        onClick={(e) => {
          e.preventDefault()
          onClick(tab)
        }}
        style={{
          padding: '0.75rem 1.5rem',
          color: isSelectedTab ? '#402080' : '#3E3F40',
          background: isSelectedTab ? '#ECE9F2' : 'transparent',
          borderRadius: isSelectedTab ? '0.25rem 0.25rem 0 0' : 0,
          borderBottom: isSelectedTab ? '2px solid #402080' : 0,
          cursor: 'pointer',
        }}
      >
        {tab.value}
      </div>
    )
  })
}
export const getCheckboxOrRadioComponent = (
  isSelectedItemSingleSelect: boolean,
  radioPropsData: any = {},
  checkboxPropsData: any = {},
) => {
  if (!isSelectedItemSingleSelect) return <MUICheckbox {...checkboxPropsData} />
  return <RadioInputComponent {...radioPropsData} />
}
export const renderReadOnlyView = (
  formData: IFieldTitleValue[],
  isBtnDisable: boolean,
  nonMandatoryFields?: string[],
) => {
  return formData?.map((item, idx) => {
    const { title, value, fieldName, required } = item
    let isFieldRequired = required
    if (nonMandatoryFields?.length && nonMandatoryFields?.includes(fieldName))
      isFieldRequired = false
    const isLastItem = idx === formData.length - 1
    const borderStyle = isBtnDisable && isLastItem ? { borderBottom: 0 } : {}

    return (
      <div key={fieldName} className='d-flex align-items-start row-title-value' style={borderStyle}>
        {getTitleValue(title, value, isFieldRequired)}
      </div>
    )
  })
}
export const displayTabDetailAndForm = <T extends FieldValues>({
  isBtnDisable,
  selectedTabIndex,
  methods,
  conditionalFieldsToShow,
  definitionInfo,
  formData,
  formDetail,
  pageType,
}: DisplayTabDetailAndFormProps<T>) => {
  const { VIEW } = CONSTANTS.PAGE_TYPE

  if (pageType === VIEW) {
    return renderReadOnlyView(formData, isBtnDisable)
  }

  if (selectedTabIndex === -1) return null

  const selectedForm = definitionInfo?.data?.tabs?.[selectedTabIndex]
  const selectedFormData = selectedForm?.tabContent?.formData
  const selectedFormLabel = selectedFormData?.[0]?.label

  return (
    <FormProvider {...methods}>
      <Suspense fallback={<Loader />} key='header_title'>
        <div className='w-100' style={{ marginBottom: '-1.5rem' }}>
          <DynamicFormCreation
            categoryFormData={formDetail}
            formData={selectedFormData}
            formLabel={selectedFormLabel}
            conditionalFieldsToShow={conditionalFieldsToShow}
          />
        </div>
      </Suspense>
    </FormProvider>
  )
}
export const showSubmitCtaBtn = (pageType: string, disabled: boolean, onClick: () => void) => {
  const { VIEW } = CONSTANTS.PAGE_TYPE
  if (pageType === VIEW) return null
  return (
    <div className='footer'>
      <div className='submit-btn-wrapper'>
        <div className='submit-btn'>
          <MUIButton
            size='large'
            type={'button'}
            label={'Save'}
            disabled={disabled}
            variant='contained'
            width={150}
            handleClickCB={onClick}
          />
        </div>
      </div>
    </div>
  )
}
export const getFilterTitle = (selectedTab: string, filterType: 'type' | 'layout') => {
  return (
    <Typography
      sx={{
        fontSize: '0.875rem',
        color: '#697282',
        lineHeight: '26px',
        paddingBottom: '0.75rem',
      }}
    >
      {`Choose a ${selectedTab} ${filterType} to filter your search results`}
    </Typography>
  )
}
export const getItemModalSubTitle = (
  selectedTab: string,
  subtitleType: 'categories' | 'search',
  paddingBottom?: string,
) => {
  return (
    <Typography
      sx={{
        fontSize: '1.125rem',
        color: '#191D23',
        lineHeight: '28px',
        fontWeight: 600,
        paddingBottom: paddingBottom ?? '0.25rem',
        textTransform: 'capitalize',
      }}
    >
      {`${selectedTab} ${subtitleType}`}
    </Typography>
  )
}
export const renderFilterList = ({
  filterList,
  onClick,
  selectedValue,
  keyType = 'key',
  shouldHover = false,
}: RenderFilterList) => {
  if (filterList.length === 0) return null

  return (
    <ul className={Styles['filter-item-list']}>
      {filterList.map((item) => {
        const isActive = item[keyType] === selectedValue
        return (
          <li
            data-testid={`${item[keyType]?.replace(/\s/g, '')}Filter`}
            key={item.key}
            aria-hidden='true'
            className={`${isActive ? Styles['active'] : ''} ${
              shouldHover ? Styles['hover-item'] : ''
            }`}
            onClick={() => onClick(item[keyType])}
          >
            <span>{item.value}</span>
          </li>
        )
      })}
    </ul>
  )
}
